import { ChangeEvent, ClipboardEvent, ReactNode, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Stack, StackItem, Link } from '@fluentui/react';
import { Eye20Filled, EyeOff20Filled } from '@fluentui/react-icons';
import { Button, Input, FluentProvider, LargeTitle, Subtitle2Stronger, Body1, Body2, Subtitle2, Body1Strong, Image, PresenceBadge, Title1, Caption1Strong, Body1Stronger, Badge, Tooltip, Spinner, Card, Title3 } from '@fluentui/react-components';
import { SkeletonPasswordReset } from '../../components/skeleton/skeletonPasswordReset';
import { SkeletonMailSent } from '../../components/skeleton/skeletonMailSent';
import { SkeletonNewPassword } from '../../components/skeleton/skeletonNewPassword';
import { sendPasswordResetEmail, submitNewPassword } from 'supertokens-web-js/recipe/emailpassword';
import { doesEmailExist } from 'supertokens-web-js/recipe/emailpassword';
import { confirmCard, authStyle, fitcontentWidth, halfWidth, logoCSS, newLightTheme, parentHome, backgroundPosition } from '../../styles/customStyles';
import PasswordStrengthIndicator from './passwordstrengthindicator';
import apiCall from '../../utility/api';

interface iUser {
  [x: string]: ReactNode;
  email: string;
  password: string;
  reenteredPassword: string;
  magicLinkEmail: string;
  otp: string;
  phone: string;
};

interface iCheckPasswordMatch {
  [x: string]: ReactNode; 
  email?: string; 
  password: any; 
  reenteredPassword: any; 
  magicLinkEmail?: string; 
  otp?: string; 
  phone?: string; 
};

export const userActive = async (user_email: string) => {
  try {
    const response = await apiCall('/users/api/updateUserStatus', { user_email }); // Using resetEmail to updateUserStatus
    console.log('API Response: ', response);
  } catch (error) { // Handle unexpected errors, log them, and potentially notify the user
    console.error('An unexpected error occurred:', (error as Error).message);
  }
};

const PasswordReset = () => {
  const Navigate = useNavigate();
  const users = { email: '', password: '', reenteredPassword: '', magicLinkEmail: '', otp: '', phone: '' };

  const [user, setUser] = useState<iUser>(users);
  const [isOnline, setIsOnline] = useState(true);
  const [isdisable, setIsdisable] = useState(true);
  const [pswStrengthFlag, setPswStrengthFlag] = useState(true);
  const [disabled, setDisabled] = useState(false);
  const [navigate, setNavigate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [tokenError, setTokenError] = useState(false);
  const [messagebar2, setmessagebar2] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [emailError, setEmailError] = useState(false);
  const [passwordFocused, setPasswordFocused] = useState(false);
  const [verifiedNavigation, setVerifiedNavigation] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [passwordResetMailSent, setpasswordResetMailSent] = useState(false);
  const [confirmPasswordFocused, setConfirmPasswordFocused] = useState(false);
  const [queryErrorMessage, setQueryErrorMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [formerror, setFormError] = useState('');

  const searchParams = new URLSearchParams(window.location.search);
  let resetEmail = searchParams.get('email');
  if (resetEmail === null) { resetEmail = user.email; }

  console.log('Reset email: ', resetEmail);
  const search = window.location.search;
  const params = new URLSearchParams(search);

  let useremail = params.get('email');
  useEffect(() => {
    console.log("Useremail's useEffects: ", useremail);
  }, [useremail]);

  function isEdge() {
    const isEdgeBrowser = /Edg(e)?\//.test(navigator.userAgent); // Check for Microsoft Edge (Chromium-based)
    const eyeSymbol = 'passwordReveal' in HTMLInputElement.prototype; // Specific check for Edge's native password reveal feature
    return isEdgeBrowser && eyeSymbol;
  };

  const handleBothSetMessage = () => { setmessagebar2(true) };
  const handlePasswordFocus = () => { setPasswordFocused(true) };
  const handleConfirmPasswordFocus = () => { setConfirmPasswordFocused(true) };
  const handleValidatedPassword = () => { setShowPassword(!showPassword); setShowConfirmPassword(!showConfirmPassword) };
  const requestResendMail = () => { setpasswordResetMailSent(false); setIsLoading(false) }

  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get('token');

  const checkPasswordMatch = (user: iCheckPasswordMatch) => {
    const { password: enter, reenteredPassword: reEnter } = user;

    console.log('Checking password: ', enter);
    console.log('Checking re enter password: ', reEnter);

    const matchPass = (
      <Badge appearance="ghost" color="success" iconPosition="after">
        <Caption1Strong> Matches </Caption1Strong>
      </Badge>
    );

    const matchFail = (
      <Badge appearance="ghost" color="danger" iconPosition="after">
        <Caption1Strong> Does not match </Caption1Strong>
      </Badge>
    );

    if (enter === reEnter && enter !== '' && reEnter !== '') {
      return (<> {setIsdisable(false)} {matchPass} </>);
    } else if ((reEnter === '' || enter === '') && reEnter !== enter) {
      return null; // Hide the message when both fields are empty or when one field was cleared
    } else {
      return matchFail;
    }
  };

  const handleInputChange = (e: ChangeEvent<HTMLInputElement>) => {
    setUser((prevUser) => {
      const { name, value } = e.target; // searchUser(e.target.value);
      const passwordStrength = checkPasswordStrength(value.trim());
      const passwordMatch = checkPasswordMatch({ ...prevUser, [name]: value.trim() });
      return { ...prevUser, [name]: value.trim(), passwordStrength, passwordMatch }
    });
  };

  const checkPasswordStrength = (password: string) => {
    const strength = [
      /[A-Z]/.test(password),
      /[\W_]/.test(password),
      (password.match(/\d/g) || []).length >= 3,
      password.length >= 8
    ].reduce((acc, condition) => acc + (condition ? 1 : 0), 0);

    setPswStrengthFlag(false);
    if (strength === 0) return (<Badge appearance="ghost" color="severe"> <Caption1Strong> Very Weak </Caption1Strong> </Badge>);
    if (strength === 1) return (<Badge appearance="ghost" color="danger"> <Caption1Strong> Weak </Caption1Strong> </Badge>);
    if (strength === 2) return (<Badge appearance="ghost" color="warning"> <Caption1Strong> Moderate </Caption1Strong> </Badge>);
    if (strength === 3) return (<Badge appearance="ghost" color="success"> <Caption1Strong> Strong </Caption1Strong> </Badge>);
    setPswStrengthFlag(true);

    return (<Badge appearance="ghost" color="success"> <Caption1Strong> Very Strong </Caption1Strong> </Badge>);
  };

  async function checkEmail(email: string) {
    try { // Check if email exists in the system
      const { doesExist: yes } = await doesEmailExist({ email });
      return yes ? true : (setFormError("Email doesn't exist. Please contact admin."), false);
    } catch (error: any) {
      const { isSuperTokensGeneralError: isError, message } = error;
      setFormError(isError === true ? message : 'Oops! Something went wrong');
      return false;
    }
  };

  const sendEmailClicked = async () => {
    setDisabled(true);
    setIsLoading(true);
    setEmailError(false);
    setFormError('');

    const { email } = user;
    const isValidEmail = await checkEmail(email);

    if (!isValidEmail) {
      setDisabled(false);
      setIsLoading(false);
      return;
    }

    console.log('Sending password to user email...');

    if (!!email) {
      try {
        const response = await sendPasswordResetEmail({
          formFields: [{
            id: 'email',
            value: email
          }]
        });

        if (response.status === 'FIELD_ERROR') {
          response.formFields.forEach(({ id, error }) => {
            if (id === 'email') {
              // window.alert(error);
              setFormError(error);
              setIsLoading(false);
              setDisabled(false);
            }
          });
        } else if (response.status === 'PASSWORD_RESET_NOT_ALLOWED') {
          setIsLoading(false);
          setDisabled(false);
        } else { // Check for existing user before setting passwordResetMailSent
          setDisabled(false);
          setEmailError(false);
          setIsLoading(true);
          setpasswordResetMailSent(true);
        }
      } catch (error) {
        // handleSuperTokensError(error);
        console.log('Checking the error at forgot password email', error);
        setFormError('Oops! Something went wrong.');
        setIsLoading(false);
        setDisabled(false);
        // await userActivity("Update",`{"summary":"Password updated successfully!"}`, 'Failure', userID);
      }
    } else {
      setIsLoading(false);
      setDisabled(false);
      setFormError('Field is not optional');
    }
  };

  const newPasswordEntered = async () => {
    const { password: enter, reenteredPassword: reEnter } = user;
    if (enter.length < 8) setErrorMessage('Password must be atleast 8 characters long.');
    if (reEnter !== enter) return; // Return to prevent further execution
    if (!/[A-Z]/.test(enter)) return { isValid: false, error: 'Password must contain at least one capital letter.' };
    if (!/[a-z]/.test(enter)) return { isValid: false, error: 'Password must contain at least one small letter.' };

    try {
      const response = await submitNewPassword({
        formFields: [{
          id: 'password',
          value: enter
        }]
      });
      const { status } = response;
      if (status === 'FIELD_ERROR') {
        response.formFields.forEach(({ id, error }) => id === 'password' ? setErrorMessage(error) : null);
      } else if (status === 'RESET_PASSWORD_INVALID_TOKEN_ERROR') {
        setVerifiedNavigation(true);
        setTokenError(true);
        setmessagebar2(false);
        console.log('Checking: ', status);
      } else { // Show success toast
        setmessagebar2(true);
        await new Promise(resolve => { setTimeout(() => { resolve(true) }, 1000); }); // Resolves after toast and state updates
        setVerifiedNavigation(true);
      }
    } catch (error: any) {
      const { isSuperTokensGeneralError: isError, message } = error;
      setErrorMessage(isError === true ? message : 'Oops! Something went wrong with password reset.');
      setNavigate(true);
    }
  };

  useEffect(() => {
    console.log('Error: ', errorMessage)
  }, [errorMessage]);

  useEffect(() => {
    localStorage.clear();
    const checkConnection = () => { setIsOnline(navigator.onLine); };
    checkConnection();
    window.addEventListener('online', checkConnection);
    window.addEventListener('offline', checkConnection);
    return () => {
      window.removeEventListener('online', checkConnection);
      window.removeEventListener('offline', checkConnection);
    };
  }, []);

  useEffect(() => {
    if (navigate) { Navigate('/'); }
  }, [navigate]);

  const pageData = () => {
    const asterix = <Image src="/Dashboard/image/required.svg" />;

    const brandLogo = (
      <StackItem style={logoCSS}>
        <Stack style={fitcontentWidth} tokens={{ padding: 10 }}>
          <Image src="/Dashboard/image/signinLogo.svg" />
        </Stack>
      </StackItem>
    );

    const backgroundImage = (
      <Stack style={backgroundPosition}> <Image block src="/Dashboard/authBackground.png" alt='Background Image' height={`100%`} /> </Stack>
    );

    const requestResetPassword = () => {
      const requestReset = () => {
        const formerrorBadge = formerror && (<Badge appearance="ghost" color="danger"> <Body1> {formerror} </Body1> </Badge>);
        const emailErrorData = emailError && (<Badge appearance="ghost" color="danger"> <Body1> Email not found </Body1> </Badge>);
        const isLoadingData = isLoading && (<Spinner delay={1000} size="extra-tiny" label="Please wait for a while...." />);

        return (
          <>
            <StackItem>
              <Stack wrap>
                <Subtitle2Stronger align="justify">
                  If you've forgotten your password, don't worry we've got you covered.
                </Subtitle2Stronger>
              </Stack>
            </StackItem>
            <StackItem>
              <Stack wrap>
                <Body1 align="justify">
                  Please enter your Email Address below, and we'll send you a secure link to reset your Password.
                </Body1>
              </Stack>
            </StackItem>
            <StackItem> <Subtitle2> Email </Subtitle2> </StackItem>
            <Stack>
              <Input placeholder="Enter Email Here" type="text" id="email" name="email" value={user.email} onChange={handleInputChange} />
            </Stack>
            {formerrorBadge}
            {emailErrorData}
            <StackItem grow shrink>
              <Button type="button" size="medium" appearance="primary" onClick={sendEmailClicked} disabled={disabled} shape="circular">
                Email Me
              </Button>
            </StackItem>
            {isLoadingData}
          </>
        )
      };

      const waitReset = () => (
        <Stack wrap tokens={{ childrenGap: 20 }}>
          <StackItem>
            <PresenceBadge size="extra-large" />
            <Title1> Thank you. </Title1>
          </StackItem>
          <StackItem>
            <Stack wrap tokens={{ childrenGap: 10 }}>
              <Body2 align="justify">
                We will send you a Password reset link, If the Email ID provided <b>{user.email}</b> matches the one we have on
                records. Kindly check your Email for the next steps. If the Email Address is incorrect,&nbsp;
                <Body2> <Link onClick={requestResendMail}> Click here </Link> </Body2> to make the necessary changes.
              </Body2>
              <StackItem>
                <Stack tokens={{ childrenGap: 10 }} horizontal>
                  <StackItem> 1. </StackItem>
                  <StackItem>
                    <Body2 align="justify"> Check your Junk/Spam message folder for an Email from <b>noreply@nanoheal.com</b> </Body2>
                  </StackItem>
                </Stack>
              </StackItem>
              <StackItem>
                <Stack tokens={{ childrenGap: 10 }} horizontal>
                  <StackItem> 2. </StackItem>
                  <Body2 align="justify"> Make sure that the Email ID that you have entered matches the one used while registering. </Body2>
                </Stack>
              </StackItem>
            </Stack>
          </StackItem>
        </Stack>
      )

      return (
        <StackItem>
          <Stack tokens={{ childrenGap: 15, padding: '20px 80px' }}>
            {passwordResetMailSent ? waitReset() : requestReset()}
          </Stack>
        </StackItem>
      );
    };

    const updateResetPassword = () => {
      const resetFailed = () => (
        <Card style={confirmCard}>
          <Stack tokens={{ childrenGap: 15, padding: 20 }}>
            <Title3 align="center"> The password reset link you used is no longer valid. </Title3>
            <Stack wrap>
              <Body1 align="center">
                Please go to the <Link to="/auth/reset-password">
                  <Stack horizontal tokens={{ childrenGap: 40 }}>
                    <StackItem> <Body1 underline onClick={() => Navigate("/auth/reset-password")}> Forgot Password </Body1> </StackItem>
                  </Stack>
                </Link> page and reset your password again.
              </Body1>
            </Stack>
            <StackItem>
              <Button type="button" appearance="primary" onClick={() => { setNavigate(true) }} shape="circular">
                Login
              </Button>
            </StackItem>
          </Stack>
        </Card>
      );

      const resetSuccess = () => (
        <Card style={confirmCard}>
          <Stack tokens={{ childrenGap: 15, padding: 20 }}>
            <Title3 align="center"> Password Updated Successfully! </Title3>
            <Stack wrap>
              <Body1 align="center"> Your password has been changed. You can now use your new password Login to your account. </Body1>
            </Stack>
            <StackItem>
              <Button type="button" appearance="primary" onClick={() => { setNavigate(true) }} shape="circular">
                Login
              </Button>
            </StackItem>
          </Stack>
        </Card>
      );

      const matchPassword = () => {
        const preventClips = (e: ClipboardEvent) => e.preventDefault();

        const inputPrimary = () => {
          const inputContentAfter = () => {
            const buttonIcon = (
              <Tooltip withArrow relationship="label" content={showPassword ? "Hide Password" : "Show Password"}>
                {showPassword ? <EyeOff20Filled /> : <Eye20Filled />}
              </Tooltip>
            );
            const badgeData = <Button size="small" appearance="transparent" onClick={handleValidatedPassword} icon={buttonIcon} shape="circular" />;
            return passwordFocused && !isEdge() && badgeData;
          }
          return (
            <Input
              placeholder="Enter Password" id="password" name="password" type={showPassword ? 'text' : 'password'} value={user.password}
              onChange={handleInputChange} onFocus={handlePasswordFocus} onPaste={preventClips} onCopy={preventClips} onCut={preventClips}
              contentAfter={inputContentAfter()} required
            />
          );
        };

        const inputSecondary = () => {
          const inputContentAfter = () => {
            const buttonIcon = (
              <Tooltip withArrow relationship="label" content={showConfirmPassword ? "Hide Password" : "Show Password"}>
                {showConfirmPassword ? <EyeOff20Filled /> : <Eye20Filled />}
              </Tooltip>
            );
            const badgeData = <Button size="small" appearance="transparent" onClick={handleValidatedPassword} icon={buttonIcon} shape="circular" />;
            return confirmPasswordFocused && !isEdge() && badgeData;
          }
          return (
            <Input
              placeholder="Re-Enter Password" id="reenteredPassword" name="reenteredPassword" type={showConfirmPassword ? 'text' : 'password'}
              value={user.reenteredPassword} onChange={handleInputChange} onFocus={handleConfirmPasswordFocus} onPaste={preventClips}
              onCopy={preventClips} onCut={preventClips} contentAfter={inputContentAfter()}
            />
          );
        };

        const queryErrorMessageData = queryErrorMessage && (<Badge appearance="ghost" color="danger"> <Body2> {queryErrorMessage} </Body2> </Badge>);

        return (
          <Stack tokens={{ childrenGap: 15, padding: 20 }}>
            <LargeTitle> Congratulations! </LargeTitle>
            <StackItem>
              <Stack wrap>
                <StackItem>
                  <Body1> Please proceed to set your new password. </Body1>
                </StackItem>
              </Stack>
            </StackItem>
            <Stack verticalFill tokens={{ childrenGap: 10 }}>
              <StackItem> <Body1Stronger> New Password </Body1Stronger> {asterix} </StackItem>
              {inputPrimary()}
            </Stack>
            <PasswordStrengthIndicator password={user.password} />
            <Stack verticalFill tokens={{ childrenGap: 10 }}>
              <StackItem> <Body1Stronger> Re-enter Password </Body1Stronger> {asterix} </StackItem>
              {inputSecondary()}
            </Stack>
            <Body1Strong> Password Status: {user.passwordMatch} </Body1Strong>
            <StackItem>
              <Button type="button" appearance="primary" disabled={isdisable ? true : false} onClick={newPasswordEntered} onClickCapture={handleBothSetMessage} shape="circular">
                Continue
              </Button>
            </StackItem>
            {queryErrorMessageData}
          </Stack>
        );
      }

      return verifiedNavigation ? (tokenError ? resetFailed() : resetSuccess()) : matchPassword();
    };

    return (
      <FluentProvider theme={newLightTheme} style={parentHome}>
        <Stack horizontal style={parentHome}>
          <Stack style={halfWidth} horizontalAlign="start">
            {brandLogo}
            <Stack grow style={authStyle}>
              <Stack tokens={{ childrenGap: 80 }} style={authStyle}>
                <Stack style={authStyle} verticalAlign='center' horizontalAlign='center'>
                  {token ? updateResetPassword() : requestResetPassword()}
                </Stack>
              </Stack>
            </Stack>
            {backgroundImage}
          </Stack>
        </Stack>
      </FluentProvider>
    )
  };

  const pageSkeleton = token ? <SkeletonNewPassword /> : (passwordResetMailSent ? <SkeletonMailSent /> : <SkeletonPasswordReset />);

  return isOnline ? pageData() : pageSkeleton;
};

export default PasswordReset;
